import dayjs from "dayjs";
import type {
  EducationDetailDto,
  VirtualClassroomDto,
} from "../types/client-education-detail.types";

/**
 * Sınıfların (sanal/fiziksel oturumların) tarih sınırlarını toplar:
 * hem `scheduledTimes` (date + start/endTime) hem de `startDate`/`endDate`
 * alanlarından en erken başlangıç ve en geç bitişi türetir.
 */
function collectClassroomBounds(classrooms?: VirtualClassroomDto[] | null): {
  start: string | null;
  end: string | null;
} {
  let minStart: dayjs.Dayjs | null = null;
  let maxEnd: dayjs.Dayjs | null = null;

  const consider = (start?: dayjs.Dayjs, end?: dayjs.Dayjs) => {
    if (start?.isValid() && (!minStart || start.isBefore(minStart))) minStart = start;
    if (end?.isValid() && (!maxEnd || end.isAfter(maxEnd))) maxEnd = end;
  };

  for (const vc of classrooms ?? []) {
    const times = vc.scheduledTimes ?? [];
    for (const t of times) {
      if (!t.date) continue;
      consider(
        dayjs(`${t.date}T${t.startTime ?? "00:00"}`),
        dayjs(`${t.date}T${t.endTime ?? "23:59"}`),
      );
    }
    if (vc.startDate || vc.endDate) {
      consider(
        vc.startDate ? dayjs(vc.startDate) : undefined,
        vc.endDate ? dayjs(vc.endDate) : undefined,
      );
    }
  }

  return {
    start: minStart ? (minStart as dayjs.Dayjs).toISOString() : null,
    end: maxEnd ? (maxEnd as dayjs.Dayjs).toISOString() : null,
  };
}

/**
 * Eğitim detayında gösterilecek başlangıç/bitiş tarihini çözer.
 *
 * Öncelik eğitimin kendi `startDate`/`endDate` alanlarındadır; eksik olan(lar)
 * için sınıfların (oturumların) tarihlerinden türetilir (en erken başlangıç /
 * en geç bitiş). Böylece "eğitimin tarihi yoksa sınıfınki görünsün" gereği
 * karşılanır ve başlangıç gösterilen her yerde bitiş de doldurulabilir.
 */
export function resolveEducationDisplayDates(
  education: Pick<EducationDetailDto, "startDate" | "endDate" | "virtualClassrooms">,
): { start: string | null; end: string | null } {
  let start = education.startDate ?? null;
  let end = education.endDate ?? null;

  const bounds = collectClassroomBounds(education.virtualClassrooms);
  if (!start) start = bounds.start;
  if (!end) end = bounds.end;

  // Bitiş eksik ya da başlangıçla aynı ana denk geliyorsa (tek an), sınıf/oturum
  // tarihlerinden daha geç bir bitiş türetilebiliyorsa onu kullan — böylece
  // "eğitim ne zaman bitiyor" her zaman görünür kalır.
  if (
    start &&
    bounds.end &&
    (!end || (dayjs(end).isValid() && dayjs(start).isValid() && dayjs(end).isSame(dayjs(start)))) &&
    dayjs(bounds.end).isAfter(dayjs(start))
  ) {
    end = bounds.end;
  }

  return { start, end };
}
